import * as Sentry from '@sentry/nextjs'

import { EtaDistanceResult, EtaLocation } from '@/feature/eta'
import { formatDurationText } from '@/feature/eta/utils/Eta.utils'
import { ObservatoryEtaSettings } from '@/feature/observatory'

export const calculateEtaAndDistance = async (
  origin: EtaLocation,
  destination: EtaLocation,
  routeLayerConfig: ObservatoryEtaSettings,
  organization: string | undefined,
): Promise<EtaDistanceResult> => {
  // Routes API rejects timestamps not strictly in the future
  //This buffer adds 5min to our request to ensure it is in the future
  const departureTime = new Date(Date.now() + 300_000)

  const routeModifiers = {
    avoidFerries: routeLayerConfig.parameters.avoid.includes('ferries'),
    avoidHighways: routeLayerConfig.parameters.avoid.includes('highways'),
    avoidTolls: routeLayerConfig.parameters.avoid.includes('tolls'),
  }

  try {
    const { routes } = await google.maps.routes.Route.computeRoutes({
      origin: new google.maps.LatLng(origin.latitude, origin.longitude),
      destination: new google.maps.LatLng(destination.latitude, destination.longitude),
      travelMode: 'DRIVING',
      routeModifiers,
      departureTime,
      routingPreference: 'TRAFFIC_AWARE_OPTIMAL',
      trafficModel: 'pessimistic',
      fields: ['durationMillis', 'distanceMeters', 'path', 'viewport', 'legs'],
    })

    const route = routes?.[0]
    const leg = route?.legs?.[0]
    const durationMillis = leg?.durationMillis ?? route?.durationMillis
    const result: EtaDistanceResult = {
      duration:
        durationMillis != null
          ? {
              text: formatDurationText(Math.round(durationMillis / 1000)),
              value: Math.round(durationMillis / 1000),
            }
          : undefined,
      distance: leg?.localizedValues?.distance ?? undefined,
      distanceMeters: leg?.distanceMeters ?? route?.distanceMeters,
      overviewPath: route?.path,
      originAddress: undefined,
      destinationAddress: undefined,
      departureTime,
      bounds: route?.viewport ?? undefined,
    }

    Sentry.logger.info(`ETA calculated for ${organization || ''}`, {
      origin: origin,
      destination: destination,
      result: result,
    })

    return result
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'eta', api: 'ComputeRoutes' },
      extra: {
        organization,
        origin: { latitude: origin.latitude, longitude: origin.longitude, type: origin.type },
        destination: {
          latitude: destination.latitude,
          longitude: destination.longitude,
          type: destination.type,
        },
        routeModifiers,
        departureTime: departureTime.toISOString(),
      },
    })
    throw error
  }
}
